import { useState } from 'react';
import {
  FileText,
  AlertTriangle,
  Building2,
  Globe,
  Edit2,
  Trash2,
  MoreVertical,
  RefreshCw,
  CheckCircle2,
  Tag,
  Calendar,
  User,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { KbEntry } from '@/hooks/useKnowledgeBase';
import { sourceTypeMeta, isFirmSpecific } from '../kbDisplay';

export interface KbEntryCardProps {
  entry: KbEntry;
  onEdit: () => void;
  onDelete: () => void;
  onMarkStale: () => void;
  onClearStale: () => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  className?: string;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function truncateText(text: string | null, maxLength: number): string {
  if (!text) return '—';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}

export function KbEntryCard({
  entry,
  onEdit,
  onDelete,
  onMarkStale,
  onClearStale,
  isExpanded = false,
  onToggleExpand,
  className,
}: KbEntryCardProps) {
  const [showActions, setShowActions] = useState(false);

  const sourceTypeBadge = sourceTypeMeta(entry.source_type);

  return (
    <Card
      className={cn(
        'hover:shadow-md transition-shadow',
        entry.is_stale && 'border-amber-300 bg-amber-50/50',
        className
      )}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <FileText
              className={cn(
                'h-5 w-5 mt-0.5 shrink-0',
                entry.is_stale ? 'text-amber-600' : 'text-primary'
              )}
            />
            <div className="min-w-0 flex-1">
              {/* Question Text */}
              <p
                className={cn(
                  'font-medium text-sm',
                  !isExpanded && 'line-clamp-2',
                  onToggleExpand && 'cursor-pointer hover:text-primary'
                )}
                onClick={onToggleExpand}
                title={entry.question_text}
              >
                {isExpanded ? entry.question_text : truncateText(entry.question_text, 150)}
              </p>

              {/* Metadata Row */}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {/* Source Type Badge */}
                <span
                  className={cn(
                    'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                    sourceTypeBadge.badgeClass
                  )}
                >
                  {sourceTypeBadge.label}
                </span>

                {/* Stale Badge */}
                {entry.is_stale && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                    <AlertTriangle className="h-3 w-3" />
                    Stale
                  </span>
                )}

                {/* Firm Scope */}
                {isFirmSpecific(entry) ? (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Building2 className="h-3 w-3" />
                    Firm #{entry.firm_id}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Globe className="h-3 w-3" />
                    Global
                  </span>
                )}

                {/* Category */}
                {entry.category && (
                  <span className="text-xs text-muted-foreground">
                    <span className="font-medium">{entry.category}</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Actions Menu */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setShowActions(!showActions)}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>

            {showActions && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowActions(false)}
                />
                <div className="absolute right-0 top-8 z-20 w-40 bg-background border rounded-md shadow-lg py-1">
                  <button
                    className="w-full px-3 py-1.5 text-sm text-left hover:bg-muted flex items-center gap-2"
                    onClick={() => {
                      setShowActions(false);
                      onEdit();
                    }}
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                    Edit
                  </button>
                  {entry.is_stale ? (
                    <button
                      className="w-full px-3 py-1.5 text-sm text-left hover:bg-muted flex items-center gap-2 text-green-600"
                      onClick={() => {
                        setShowActions(false);
                        onClearStale();
                      }}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Mark as Current
                    </button>
                  ) : (
                    <button
                      className="w-full px-3 py-1.5 text-sm text-left hover:bg-muted flex items-center gap-2 text-amber-600"
                      onClick={() => {
                        setShowActions(false);
                        onMarkStale();
                      }}
                    >
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Mark as Stale
                    </button>
                  )}
                  <div className="border-t my-1" />
                  <button
                    className="w-full px-3 py-1.5 text-sm text-left hover:bg-muted flex items-center gap-2 text-red-600"
                    onClick={() => {
                      setShowActions(false);
                      onDelete();
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Response Text */}
        <div className="bg-muted/50 rounded-md p-3 mb-3">
          <p
            className={cn(
              'text-sm text-muted-foreground',
              !isExpanded && 'line-clamp-3'
            )}
          >
            {isExpanded ? (entry.response_text ?? '—') : truncateText(entry.response_text, 200)}
          </p>
          {!isExpanded && (entry.response_text?.length ?? 0) > 200 && onToggleExpand && (
            <button
              className="text-xs text-primary hover:underline mt-1"
              onClick={onToggleExpand}
            >
              Show more
            </button>
          )}
        </div>

        {/* Tags */}
        {entry.tags.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mb-3">
            <Tag className="h-3 w-3 text-muted-foreground" />
            {entry.tags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs py-0 px-1.5">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {/* Stale Reason */}
        {entry.is_stale && entry.stale_reason && (
          <div className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-1 mb-3">
            <span className="font-medium">Stale reason:</span> {entry.stale_reason}
          </div>
        )}

        {/* Footer Metadata */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Created {formatDate(entry.created_at)}
            </span>
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {entry.created_by}
            </span>
          </div>
          {entry.updated_at !== entry.created_at && (
            <span className="flex items-center gap-1">
              <RefreshCw className="h-3 w-3" />
              Updated {formatDate(entry.updated_at)}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
